import React from 'react';

import {
  Alert,
  Box,
  Button,
  CloseButton,
  Heading,
  Icon,
  IconButton,
  Modal,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconDownloadFilled, IconFilterFilled } from '@zillow/constellation-icons';

import { LoadingConstants } from '@zg-rentals/rental-manager-env';
import MultiInvoicePayModal from '../components/modals/MultiInvoicePayModal';
import { useInvoiceDownloader } from '../hooks/useInvoiceDownloader';
import { useAppMaintenance } from '../hooks/useMaintenance';
import { type AdproductsZuoraBillingInvoiceDto } from '@zg-rentals/ts-adproducts-client';
import type { InvoiceListFilterConfig } from '../hooks/useInvoiceList';
import { InvoiceFilter } from '../pages/BillingInvoicePage/InvoiceFilter';

const notificationTones: Record<string, 'info' | 'success' | 'critical'> = {
  [LoadingConstants.LOADING]: 'info',
  [LoadingConstants.SUCCESS]: 'success',
  [LoadingConstants.ERROR]: 'critical',
};

export type BillingTopBannerProps = {
  data?: Array<AdproductsZuoraBillingInvoiceDto>;
  selected?: Set<number>;
  deselectAll?: () => void;
  refreshInvoices?: () => void;
  isPaymentDue?: boolean;
  headerLeft?: React.ReactNode;
  filters?: InvoiceListFilterConfig;
  setFilters?: (newFilters: InvoiceListFilterConfig) => void;
};

const BillingTopBanner = ({
  data = [],
  selected = new Set<number>(),
  deselectAll,
  refreshInvoices,
  isPaymentDue = false,
  headerLeft,
  filters,
  setFilters,
}: BillingTopBannerProps) => {
  const { downloadState, downloadErrorMessage, getInvoices, resetDownloadState } = useInvoiceDownloader();
  const { isMaintenanceMode } = useAppMaintenance();
  const [showModal, setShowModal] = React.useState(false);
  const [showFilterModal, setShowFilterModal] = React.useState(false);

  const handleNotificationCloseClick = () => resetDownloadState();

  const defaultFilters: InvoiceListFilterConfig = { property: [], city: [], state: [] };
  const [localFilters, setLocalFilters] = React.useState<InvoiceListFilterConfig>(defaultFilters);
  const activeFilters = filters ?? localFilters;
  const activeSetFilters = setFilters ?? setLocalFilters;
  const { size } = selected;
  React.useEffect(() => {
    if (size === 0) {
      handleNotificationCloseClick();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [selected]);

  const handleDownloadClick = () => {
    const invoiceNumbers = [];
    for (const i of selected) {
      invoiceNumbers.push(data[i].invoiceNumber);
    }
    getInvoices(invoiceNumbers);
  };

  const getNotificationContent = (downloadState: string, downloadErrorMessage: string) => {
    switch (downloadState) {
      case LoadingConstants.LOADING:
        return 'Downloading invoice files...';
      case LoadingConstants.SUCCESS:
        return 'Download successful!';
      case LoadingConstants.ERROR:
        return downloadErrorMessage;
      default:
        return '';
    }
  };

  const hasSelection = data.length > 0 && size > 0;

  let invoiceBalance = 0;
  if (hasSelection) {
    for (const i of selected) {
      invoiceBalance += data[i].balance;
    }
  }

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <Box css={{ marginBlock: '12px' }}>
      {hasSelection && downloadState !== LoadingConstants.INITIAL && (
        <Alert
          tone={notificationTones[downloadState]}
          onClose={handleNotificationCloseClick}
          closeButton={<CloseButton />}
        >
          {getNotificationContent(downloadState, downloadErrorMessage)}
        </Alert>
      )}
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'xs' }}>
        {headerLeft}
        <IconButton
          aria-label="Filters"
          title="Filters"
          tone="neutral"
          emphasis="bare"
          size="sm"
          onClick={() => setShowFilterModal(true)}
        >
          <Icon render={<IconFilterFilled />} />
        </IconButton>
        {deselectAll && (
          <IconButton
            aria-label="Download"
            title="Download"
            tone="neutral"
            emphasis="bare"
            size="sm"
            onClick={handleDownloadClick}
            disabled={!hasSelection || isMaintenanceMode}
          >
            <Icon render={<IconDownloadFilled />} />
          </IconButton>
        )}
        {hasSelection && deselectAll && (
          <Box css={{ display: 'flex', alignItems: 'center', gap: 'xs' }}>
            <Text textStyle="body-bold">{size} selected</Text>
            <TextButton onClick={deselectAll}>Clear</TextButton>
          </Box>
        )}
        {isPaymentDue && hasSelection && invoiceBalance > 0 && (
          <Box css={{ marginInlineStart: 'auto' }}>
            <Button tone="brand" emphasis="filled" onClick={() => setShowModal(true)}>
              Pay all
            </Button>
          </Box>
        )}
      </Box>
      <Modal.Root
        open={showFilterModal}
        onOpenChange={(open) => {
          if (!open) setShowFilterModal(false);
        }}
        size={{ base: 'fullScreen', lg: 'md' }}
      >
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content>
              <Modal.Header>
                <Heading level={6}>Filters</Heading>
              </Modal.Header>
              <Modal.Body>
                <InvoiceFilter
                  filters={activeFilters}
                  setFilters={(f) => {
                    activeSetFilters(f);
                    setShowFilterModal(false);
                  }}
                  isPaymentDue={isPaymentDue}
                />
              </Modal.Body>
              <Modal.CloseButton />
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
      {showModal && deselectAll && refreshInvoices && (
        <MultiInvoicePayModal
          open={showModal}
          close={handleCloseModal}
          data={data}
          selected={selected}
          deselectAll={deselectAll}
          refreshInvoices={refreshInvoices}
        />
      )}
    </Box>
  );
};

export default BillingTopBanner;
